/**
 * useCardShare — a single hook used by all share widgets.
 *
 * Behaviour:
 *   - downloadPng(): native browser download via ?download=1 — no fetch/blob,
 *     no CORS pitfalls, works in every deployed environment.
 *   - openLinkedIn(): opens the share-offsite tab AND triggers the download,
 *     AND records a "pending confirmation" flag in localStorage so the
 *     follow-up toast can fire when the user returns to our tab.
 *   - copyLink(): writes shareUrl + caption to clipboard AND triggers download.
 *   - All three POST a fire-and-forget event to /api/share-events.
 *
 * The follow-up toast is mounted globally via <ShareConfirmListener/> — it
 * watches for the localStorage flag on visibilitychange/focus and asks
 * "Did your LinkedIn post go live?".
 */
import { useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const PENDING_KEY = 'shareLinkedInPending';

function _trackEvent(payload) {
  // Fire-and-forget — never block the share UX on telemetry.
  try {
    fetch(`${API}/api/share-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

function _nativeDownload(url, filename) {
  // Native anchor click — browser handles the download, no JS blob plumbing.
  // Backend sets Content-Disposition: attachment when ?download=1 is passed.
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'stockton-reality.png';
  // rel/target safety
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  // Defer removal so Safari has a tick to start the download
  setTimeout(() => { try { a.remove(); } catch {} }, 200);
}

/**
 * @param {object} opts
 * @param {string} opts.target — 'borough' or district code like 'TS18'
 * @param {string} opts.cardPath — path under /api/og/, e.g. 'borough-reality.png' or 'postcode-card/TS18.png'
 * @param {string} opts.shareUrl — public URL to share (the page, not the image)
 * @param {string} opts.caption — text to copy to clipboard
 * @param {string} opts.filename — suggested download filename
 */
export default function useCardShare({ target, cardPath, shareUrl, caption, filename }) {
  const cardUrl = `${API}/api/og/${cardPath}?download=1&v=${Math.floor(Date.now() / 60000)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl || '')}`;

  const downloadPng = useCallback(() => {
    _nativeDownload(cardUrl, filename);
    _trackEvent({ target, channel: 'download' });
  }, [cardUrl, filename, target]);

  const openLinkedIn = useCallback(() => {
    // 1) Drop the PNG locally so the user can attach it manually if needed.
    _nativeDownload(cardUrl, filename);
    // 2) Mark as pending — the global listener will toast on return.
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        target,
        ts: Date.now(),
      }));
    } catch {}
    // 3) Open LinkedIn in a new tab.
    window.open(liUrl, '_blank', 'noopener,noreferrer');
    // 4) Fire telemetry.
    _trackEvent({ target, channel: 'linkedin' });
  }, [cardUrl, filename, liUrl, target]);

  const copyLink = useCallback(async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(`${shareUrl}\n\n${caption}`);
      ok = true;
    } catch { /* clipboard blocked */ }
    _nativeDownload(cardUrl, filename);
    _trackEvent({ target, channel: 'copy' });
    return ok;
  }, [cardUrl, caption, filename, shareUrl, target]);

  return { cardUrl, liUrl, downloadPng, openLinkedIn, copyLink };
}

export const SHARE_PENDING_KEY = PENDING_KEY;
